import { z } from "zod";

import { RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS } from "~/screens/Scheduling/PersonalInformation/components/VisitForm/constants";
import {
  basePersonalInformationObject,
  insuranceLaterSchema,
  insuranceNowSchema,
  optionalAddressSchema,
} from "~/screens/Scheduling/PersonalInformation/validation/schema";
import {
  FILL_OPTION,
  VISIT_TYPES,
  type FillOption,
} from "~/services/scheduling/constants";
import type { VisitType } from "~/services/scheduling/types";

const personalInfoSchema = basePersonalInformationObject.pick({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  sexAtBirth: true,
  mobilePhone: true,
});

const addressSchema = optionalAddressSchema.extend({
  homeAddress2: basePersonalInformationObject.shape.homeAddress2,
  zipCode: basePersonalInformationObject.shape.zipCode,
});

const bookingNotesSchema = z.object({
  bookingNotes: z.string().optional(),
});

const referralSchema = z.object({
  referralSource: z.string().optional(),
});

const insuranceDetailsNowSchema = insuranceNowSchema.extend({
  groupNumber: z
    .string()
    .max(30, "Group number must be less than 30 characters")
    .optional(),
});

type InsuranceDetails = Partial<
  Omit<z.infer<typeof insuranceDetailsNowSchema>, "fillInsuranceInfo">
>;

const addCompanyAndCardIssues = (
  data: InsuranceDetails,
  hasPlan: boolean,
  ctx: z.RefinementCtx,
) => {
  if (!hasPlan && !data.insuranceCompanyName) {
    ctx.addIssue({
      code: "custom",
      message: "Insurance company name is required",
      path: ["insuranceCompanyName"],
    });
  }

  if (data.insuranceCardFront && !data.insuranceCardBack) {
    ctx.addIssue({
      code: "custom",
      message: "Please upload the back of the insurance card",
      path: ["insuranceCardBack"],
    });
  }
  if (data.insuranceCardBack && !data.insuranceCardFront) {
    ctx.addIssue({
      code: "custom",
      message: "Please upload the front of the insurance card",
      path: ["insuranceCardFront"],
    });
  }
};

const addPolicyHolderIssues = (
  data: InsuranceDetails,
  ctx: z.RefinementCtx,
) => {
  if (
    data.relationshipToPolicyHolder ===
    RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS.SELF
  ) {
    return;
  }

  if (!data.policyHolderFirstName) {
    ctx.addIssue({
      code: "custom",
      message: "Policy holder first name is required",
      path: ["policyHolderFirstName"],
    });
  }
  if (!data.policyHolderLastName) {
    ctx.addIssue({
      code: "custom",
      message: "Policy holder last name is required",
      path: ["policyHolderLastName"],
    });
  }
  if (!data.policyHolderDateOfBirth) {
    ctx.addIssue({
      code: "custom",
      message: "Policy holder date of birth is required",
      path: ["policyHolderDateOfBirth"],
    });
  }
};

const insuranceFieldsSchema = z.discriminatedUnion(
  "fillInsuranceInfo",
  [insuranceLaterSchema, insuranceDetailsNowSchema],
  { error: "Please select an option" },
);

const buildInsuranceSchema = (hasPlan: boolean) =>
  insuranceFieldsSchema.superRefine((data, ctx) => {
    if (data.fillInsuranceInfo === FILL_OPTION.LATER) return;

    addCompanyAndCardIssues(data, hasPlan, ctx);
    addPolicyHolderIssues(data, ctx);
  });

const emptyToUndefined = (value: unknown) => (value === "" ? undefined : value);

const followUpInsuranceNowSchema = insuranceDetailsNowSchema.extend({
  relationshipToPolicyHolder: z.string().optional(),
  memberId: z.preprocess(
    emptyToUndefined,
    insuranceNowSchema.shape.memberId.optional(),
  ),
  policyHolderDateOfBirth: z.preprocess(
    emptyToUndefined,
    insuranceNowSchema.shape.policyHolderDateOfBirth,
  ),
});

const hasInsuranceDetails = (data: InsuranceDetails) =>
  [
    data.relationshipToPolicyHolder,
    data.memberId,
    data.groupNumber,
    data.insuranceCompanyName,
    data.insuranceCardFront,
    data.insuranceCardBack,
    data.policyHolderFirstName,
    data.policyHolderLastName,
    data.policyHolderDateOfBirth,
  ].some(Boolean);

const buildFollowUpInsuranceSchema = (hasPlan: boolean) =>
  z
    .discriminatedUnion(
      "fillInsuranceInfo",
      [insuranceLaterSchema, followUpInsuranceNowSchema],
      { error: "Please select an option" },
    )
    .superRefine((data, ctx) => {
      if (data.fillInsuranceInfo === FILL_OPTION.LATER) return;
      if (!hasInsuranceDetails(data)) return;

      if (!data.relationshipToPolicyHolder) {
        ctx.addIssue({
          code: "custom",
          message: "Please select the relationship to the policy holder",
          path: ["relationshipToPolicyHolder"],
        });
      }
      if (!data.memberId) {
        ctx.addIssue({
          code: "custom",
          message: "Member ID is required",
          path: ["memberId"],
        });
      }

      addCompanyAndCardIssues(data, hasPlan, ctx);
      if (data.relationshipToPolicyHolder) addPolicyHolderIssues(data, ctx);
    });

const followUpAddressSchema = z.discriminatedUnion(
  "fillAddressInfo",
  [
    z.object({ fillAddressInfo: z.literal(FILL_OPTION.LATER) }),
    basePersonalInformationObject
      .pick({
        homeAddress1: true,
        homeAddress2: true,
        city: true,
        state: true,
        zipCode: true,
      })
      .extend({ fillAddressInfo: z.literal(FILL_OPTION.NOW) }),
  ],
  { error: "Please select an option" },
);

const patientWithEhrIdSchema = addressSchema.extend(bookingNotesSchema.shape);

const patientWithoutEhrIdSchema = patientWithEhrIdSchema
  .extend(personalInfoSchema.shape)
  .extend(referralSchema.shape);

export const buildStaffPatientInfoSchema = ({
  visitType,
  hasEhrId,
  hasPlan,
}: {
  visitType: VisitType;
  hasEhrId: boolean;
  hasPlan: boolean;
}) => {
  if (visitType === VISIT_TYPES.FOLLOW_UP) {
    return z.intersection(
      z.intersection(followUpAddressSchema, bookingNotesSchema),
      buildFollowUpInsuranceSchema(hasPlan),
    );
  }

  const patientSchema = hasEhrId
    ? patientWithEhrIdSchema
    : patientWithoutEhrIdSchema;

  if (visitType === VISIT_TYPES.COACH) {
    return patientSchema.extend({
      zipCode: optionalAddressSchema.shape.zipCode,
    });
  }

  return z.intersection(patientSchema, buildInsuranceSchema(hasPlan));
};

type InsuranceFormData = z.infer<typeof insuranceFieldsSchema>;

export type StaffPatientInfoFormData = Partial<
  z.infer<typeof patientWithoutEhrIdSchema>
> &
  Partial<
    Omit<
      Extract<InsuranceFormData, { fillInsuranceInfo: typeof FILL_OPTION.NOW }>,
      "fillInsuranceInfo"
    >
  > & {
    fillInsuranceInfo?: InsuranceFormData["fillInsuranceInfo"];
    fillAddressInfo?: FillOption;
  };
